import { useEffect, useState } from 'react'
import { TASK_TYPES } from '../../../constants/constants'

/**
 * Custom hook for managing image gallery state and logic
 * @param {Array<string>} imageAnswer - Array of image URLs to validate and display
 * @param {string} uid - Unique identifier for the survey/question
 * @param {number} questionNumber - Question number for generating download filenames
 * @returns {Object} Image gallery state and handlers
 */
export const useImageGallery = (imageAnswer, uid, questionNumber, taskType) => {
  const [maxImageInWrapper, setMaxImageInWrapper] = useState(6)
  const [imageGallery, setImageGallery] = useState([])
  const [isImageGalleryReady, setIsImageGalleryReady] = useState(false)
  const [showImage, setShowImage] = useState(false)
  const [currentImageShow, setCurrentImageShow] = useState(0)

  const filenameForDownloads =
    taskType == TASK_TYPES.planimetry.key ? `${uid}` : `${uid}-${questionNumber}`
  const overflowingImageAnswerAmount = imageGallery.length - maxImageInWrapper

  // Handle responsive breakpoints for max images in wrapper
  const handleImageWrapperMax = () => {
    if (taskType == TASK_TYPES.planimetry.key) {
      return setMaxImageInWrapper(11)
    } else {
      if (window.matchMedia('(max-width: 1265px)').matches) {
        return setMaxImageInWrapper(2)
      } else if (window.matchMedia('(max-width: 1460px)').matches) {
        return setMaxImageInWrapper(3)
      } else if (window.matchMedia('(max-width: 1655px)').matches) {
        return setMaxImageInWrapper(4)
      } else if (window.matchMedia('(max-width: 1850px)').matches) {
        return setMaxImageInWrapper(5)
      } else if (window.matchMedia('(max-width: 2000px)').matches) {
        return setMaxImageInWrapper(6)
      }
    }
  }

  // Set up responsive breakpoint listener
  useEffect(() => {
    handleImageWrapperMax()
    window.addEventListener('resize', handleImageWrapperMax)

    return () => {
      window.removeEventListener('resize', handleImageWrapperMax)
    }
  }, [])

  // Validate and clean image gallery when imageAnswer changes
  useEffect(() => {
    let cancelled = false

    if (!imageAnswer || imageAnswer.length === 0) {
      setImageGallery([])
      setIsImageGalleryReady(true)
      return () => {
        cancelled = true
      }
    }

    setIsImageGalleryReady(false)
    // Reset gallery before validating new images to avoid stale state
    setImageGallery([])

    // Validate each image URL by fetching it
    const validateImages = async () => {
      const imagePromises = imageAnswer.map(async (url) => {
        try {
          const response = await fetch(url, { method: 'GET' })
          if (response.ok) {
            return url
          }
        } catch (error) {
          console.error('Error validating image:', error)
        }
        return null
      })
      const validatedUrls = await Promise.all(imagePromises)
      if (cancelled) return
      setImageGallery(validatedUrls.filter((url) => url !== null))
      setIsImageGalleryReady(true)
    }
    validateImages()

    return () => {
      cancelled = true
    }
  }, [imageAnswer])

  // Handle opening/closing lightbox
  const handleShowImage = (value, index = 0) => {
    setCurrentImageShow(index)
    setShowImage(value)
  }

  // Handle image click to open lightbox at specific index
  const handleImageClick = (index) => {
    handleShowImage(true, index)
  }

  return {
    imageGallery,
    isImageGalleryReady,
    maxImageInWrapper,
    overflowingImageAnswerAmount,
    showImage,
    currentImageShow,
    filenameForDownloads,
    handleShowImage,
    handleImageClick
  }
}
